import { useEffect, useState } from 'react';
import { useActiveCampaign } from '@/api/recognition';
import { hasFrontlineSession, getFrontlineUser } from '@/auth/frontlineSession';
import { useCurrentUser } from '@/hooks/useCurrentUser';

export type RecoHomeTarget = '/app/brillamos' | '/app/reconocer';

export interface UseRecoHomeTargetResult {
  recoTarget: RecoHomeTarget;
  isLoading: boolean;
}

/**
 * A qué pantalla de Reconocimiento corresponde "home" según si hay campaña Brillamos activa para
 * la OU de la sesión actual (Frontline u OU de directorio ya resuelta). Única fuente compartida
 * por `LandingDecision` (bug Sesión #98 — /app exacto y el comodín /app/*) y por el guard de
 * entrada de `AppLayout` (bug Sesión #98 extendido a las rutas FIJAS /app/reconocer y
 * /app/brillamos): ambos deben decidir exactamente lo mismo, con el mismo criterio de espera —
 * duplicarlo abriría la puerta a que las tres rutas volvieran a divergir, que es justo lo que
 * causó el bug original.
 */
export function useRecoHomeTarget(): UseRecoHomeTargetResult {
  const isFrontline = hasFrontlineSession();
  const currentUser = useCurrentUser();
  // OU: Frontline → de la sesión propia (inmediata, localStorage); Sigma → del perfil de
  // directorio ya resuelto (autoritativo — no del localStorage de UserContext, que puede traer la
  // OU de un usuario anterior en un navegador compartido durante la carga).
  const ou = isFrontline ? (getFrontlineUser()?.ouId ?? undefined) : (currentUser?.ouId || undefined);

  // Margen para que el perfil/OU del usuario Sigma se resuelvan antes de decidir; si no llegan a
  // tiempo, se deja de esperar y se decide con lo que haya (Reconocimiento, el destino por
  // defecto, es seguro sin campaña confirmada).
  const [waited, setWaited] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setWaited(true), 4000);
    return () => clearTimeout(id);
  }, []);

  const campaign = useActiveCampaign(ou);
  const profileReady = isFrontline || (currentUser?.profileResolved ?? false);

  const isLoading = !waited
    && ((!isFrontline && (!profileReady || !ou)) || (Boolean(ou) && campaign.isLoading));

  const c = campaign.data;
  const brillamosActive = c?.tipo === 'Brillamos' && c?.estado === 'Activa';
  const recoTarget: RecoHomeTarget = brillamosActive ? '/app/brillamos' : '/app/reconocer';

  return { recoTarget, isLoading };
}
